import {useContext, useEffect, useState} from 'react';
import {useCookies} from 'react-cookie';
import {AuthContext} from 'src/pages/auth/context';
import {AUTH_TOKEN, CUSTOMER_ROLES} from 'src/shared/constants/AppConst';
import {shopInfoServices} from 'src/shared/services/manage-shop/shop-info-admin';
import {IShopInfoData} from 'src/shared/services/manage-shop/shop-info-admin/dtos/shop-details';
import MainUtils from 'src/shared/utils/main';

// One request per sign-in: the header, the welcome strip and the logout
// dialog share it. Once it settles, `settled`/`value` let a component that
// mounts later (another page) start with the shop instead of loading again.
let cached:
  | {
      token: string;
      request: Promise<IShopInfoData | undefined>;
      settled: boolean;
      value?: IShopInfoData;
    }
  | undefined;

const loadShop = (token: string) => {
  if (!cached || cached.token !== token) {
    const entry: NonNullable<typeof cached> = {
      token,
      settled: false,
      request: shopInfoServices
        .getShopDetails()
        .then((details) => {
          entry.settled = true;
          entry.value = details?.ShopInfo;
          return entry.value;
        })
        .catch((error) => {
          console.error(error);
          if (cached === entry) cached = undefined;
          return undefined;
        }),
    };
    cached = entry;
  }
  return cached.request;
};

const settledShop = (token?: string) =>
  token && cached?.token === token && cached.settled ? cached.value : undefined;

// Shop logos may come back as absolute URLs or as paths on the public host.
const resolveLogoUrl = (url: string) =>
  url.startsWith('http') ? url : `${process.env.REACT_APP_PUBLIC_BASE}${url}`;

// The signed-in shop admin's own shop (undefined for other users), its logo's
// URL when it has one, and `loading` while that isn't known yet (the user's
// roles, then the shop): show a placeholder then, not the generic "Admin".
export const useShopAdminShop = () => {
  const {userRolesName, rolesLoaded}: any = useContext(AuthContext);
  const [cookies] = useCookies([AUTH_TOKEN]);
  const token: string | undefined = cookies[AUTH_TOKEN];
  const isShopAdmin = Boolean(
    userRolesName?.includes(CUSTOMER_ROLES.ShopAdmin),
  );
  const [shop, setShop] = useState<IShopInfoData | undefined>(() =>
    settledShop(token),
  );
  const [shopLoaded, setShopLoaded] = useState<boolean>(() =>
    Boolean(token && cached?.token === token && cached.settled),
  );

  useEffect(() => {
    if (!isShopAdmin || !token) {
      setShop(undefined);
      setShopLoaded(false);
      return;
    }
    let active = true;
    loadShop(token).then((info) => {
      if (!active) return;
      setShop(info);
      setShopLoaded(true);
    });
    return () => {
      active = false;
    };
  }, [isShopAdmin, token]);

  const logoUrl =
    shop && !MainUtils.isEmptyValue(shop.IconUrl)
      ? resolveLogoUrl(shop.IconUrl)
      : undefined;

  const loading = Boolean(token) && (!rolesLoaded || (isShopAdmin && !shopLoaded));

  return {shop, logoUrl, loading};
};
